import { useCallback, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { EmptyPanel } from '@/components/px/EmptyPanel'
import { ErrorPanel } from '@/components/px/ErrorPanel'
import { LoadingPanel } from '@/components/px/LoadingPanel'
import { SectionLabel } from '@/components/px/SectionLabel'
import { selectActiveOrgId } from '@/features/auth/slices/sessionSlice'
import { asApiError } from '@/services/apiError'
import { useAppDispatch, useAppSelector } from '@/store'
import {
  loadTaskActivity,
  selectActivityError,
  selectActivityLoading,
  selectBoardProjectId,
  selectSelectedTask,
  selectTaskActivity
} from '../slices/boardSlice'

/**
 * The run log (AC-7). There is no agent-run list endpoint yet — this panel
 * reads TASK ACTIVITY (`GET …/projects/:projectId/activity?entityType=taskId`,
 * Decision 1) and says so plainly; it is not a run history, and its empty
 * state names that rather than fabricating fixture run rows.
 */
export function RunLogPanel() {
  const { t } = useTranslation('projectWorkspace')
  const dispatch = useAppDispatch()
  const orgId = useAppSelector(selectActiveOrgId)
  const { projectId: routeProjectId } = useParams<{ projectId: string }>()
  const boardProjectId = useAppSelector(selectBoardProjectId)
  // The route wins when it names a project (fix cycle 1, GRP-82297E9A): the
  // board slice survives the overview, so on a deep link to
  // `/project/:projectId/build` it can still hold the PREVIOUS project on
  // first paint. `/my/board` and `/my/project` carry no `:projectId` segment
  // (finding 2, GRP-B3D9F416) — there the board's loaded project is used.
  const projectId = routeProjectId ?? boardProjectId ?? undefined
  const task = useAppSelector(selectSelectedTask)
  const loading = useAppSelector(selectActivityLoading)
  const activityError = useAppSelector(selectActivityError)
  const rows = useAppSelector((state) => selectTaskActivity(state, task?.legacyId ?? null))

  useEffect(() => {
    if (orgId === null || projectId === undefined) return
    void dispatch(loadTaskActivity({ orgId, projectId }))
  }, [dispatch, orgId, projectId])

  const onRetryClicked = useCallback(() => {
    if (orgId === null || projectId === undefined) return
    void dispatch(loadTaskActivity({ orgId, projectId }))
  }, [dispatch, orgId, projectId])

  return (
    <div className="rounded-lg border border-border bg-card px-4 py-3" data-testid="run-log-panel">
      <SectionLabel>{t('board.runLog.title')}</SectionLabel>
      {/*
       * No agent-run list endpoint exists yet (epic AC-3 / spec §9 OQ3 —
       * confirmed at plan time). What renders below is task ACTIVITY, not a
       * run history; the empty state says so rather than fabricating rows.
       */}
      <p className="mt-0.5 text-xs text-muted-foreground">{t('board.runLog.subtitle')}</p>

      {task === null ? (
        <p className="mt-2 text-sm text-muted-foreground">{t('board.runLog.noSelection')}</p>
      ) : activityError !== null ? (
        // Deferred finding c2-1: a dead activity GET used to be rendered by
        // nothing (BuildQueue reads only `listError`, `BoardVerdictActions`
        // renders `error` scoped to a task, and this panel had no error
        // branch at all) — a failed read used to read as "this task has no
        // activity". An error state here, with retry, replaces that silence.
        <div className="mt-2">
          <ErrorPanel
            error={asApiError({ status: activityError.status, key: activityError.code, message: activityError.message })}
            onRetryClicked={onRetryClicked}
          />
        </div>
      ) : loading && rows.length === 0 ? (
        <div className="mt-2">
          <LoadingPanel label={t('board.runLog.loading')} />
        </div>
      ) : rows.length === 0 ? (
        <div className="mt-2">
          <EmptyPanel title={t('board.runLog.emptyTitle')} body={t('board.runLog.emptyBody')} />
        </div>
      ) : (
        <ul className="mt-2 space-y-2" data-testid="run-log-rows">
          {rows.map((row) => (
            <li key={row.id} className="text-sm">
              <div className="flex flex-wrap items-baseline gap-x-2">
                <span className="font-medium text-foreground">{row.author?.name ?? row.type}</span>
                <span className="text-xs text-muted-foreground">{row.type}</span>
              </div>
              {row.content !== undefined && (
                <p className="mt-0.5 text-sm text-ink-soft">{row.content}</p>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
